import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, tap, throwError, timeout } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { I18nService } from './i18n.service';
import { SettingsService } from './settings.service';
import { TagService } from './tag.service';
import { PushNotificationService } from './push-notification.service';
import { environment } from '../../../environments/environment';

export interface LoginResponse {
  AccountID: number;
  SessionID: string;
  Username: string;
  Password: string;
  AccountEmail: string;
  AccountOwner: string;
  UserLevel: number;
  ReportsURL: string;
  GoogleAPIKeyClient: string;
  Lang?: number;
  DateFormat?: number;
  DistanceUnits?: number;
  [key: string]: unknown;
}

export interface AdminLoginResponse {
  ID: number;
  LoginID: number;
  LoginToken: string;
  [key: string]: unknown;
}

export interface Session {
  accountId: number;
  sessionId: string;
  username: string;
  name: string;
  initials: string;
  email?: string;
  lang?: unknown;
  isAdmin?: boolean;
  adminId?: number;
  loginId?: number;
  loginToken?: string;
}

export interface SavedAccount {
  username: string;
  password: string;
  initials: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly SESSION_KEY = 'ble_session';
  private readonly REMEMBER_KEY = 'ble_remember';
  private readonly REMEMBER_PWD_KEY = 'ble_remember_pwd';
  private readonly SAVED_ACCOUNTS_KEY = 'ble_saved_accounts';
  private readonly LOGIN_URL = 'https://login.trackserver.co.uk/api/api/MobileApp/Login';
  private readonly ADMIN_LOGIN_URL = 'https://login.trackserver.co.uk/adminapi/api/Login/Login';

  private http = inject(HttpClient);
  private router = inject(Router);
  private titleService = inject(Title);
  private i18n = inject(I18nService);
  private settingsService = inject(SettingsService);
  private tagService = inject(TagService);
  private pushNotification = inject(PushNotificationService);

  isAuthenticated = signal(this.hasSession());
  currentUser = signal<Session | null>(this.getStoredSession());
  private _currentPassword = signal<string | null>(null);

  constructor() {
    const session = this.getStoredSession();
    if (session?.lang !== undefined) this.i18n.setLang(session.lang);
  }

  login(username: string, password: string, rememberMe = false): Observable<LoginResponse | AdminLoginResponse> {
    const params = new HttpParams()
      .set('AccountName', username)
      .set('AccountPassword', password)
      .set('AppName', 'TagTrackserver');

    return this.http.get<LoginResponse>(this.LOGIN_URL, { params }).pipe(
      catchError(originalErr =>
        this.http.post<AdminLoginResponse>(this.ADMIN_LOGIN_URL, { Username: username, Password: password }).pipe(
          timeout(15000),
          tap(response => this.storeAdminSession(response, username, password, rememberMe)),
          catchError(() => throwError(() => originalErr)),
        )
      ),
      tap(response => {
        if ('SessionID' in response) {
          this.storeStandardSession(response as LoginResponse, username, password, rememberMe);
        }
      }),
    );
  }

  loginAsSubAccount(username: string, password: string): Observable<LoginResponse> {
    const params = new HttpParams()
      .set('AccountName', username)
      .set('AccountPassword', password)
      .set('AppName', 'TagTrackserver');
    return this.http.get<LoginResponse>(this.LOGIN_URL, { params }).pipe(
      tap(response => {
        const admin = this.currentUser();
        const name = response.Username || username;
        const session: Session = {
          accountId: response.AccountID,
          sessionId: response.SessionID,
          username: response.Username,
          name,
          initials: name.slice(0, 2).toUpperCase(),
          lang: response.Lang,
          isAdmin: admin?.isAdmin,
          adminId: admin?.adminId,
          loginId: admin?.loginId,
          loginToken: admin?.loginToken,
        };
        const serialised = JSON.stringify(session);
        if (localStorage.getItem(this.SESSION_KEY)) {
          localStorage.setItem(this.SESSION_KEY, serialised);
        } else {
          sessionStorage.setItem(this.SESSION_KEY, serialised);
        }
        this._currentPassword.set(password);
        this.currentUser.set(session);
        this.i18n.setLang(response.Lang);
        this.settingsService.setFromLogin(response.DateFormat, response.DistanceUnits);
      }),
    );
  }

  private storeStandardSession(response: LoginResponse, username: string, password: string, rememberMe: boolean): void {
    const name = response.Username || username;
    const session: Session = {
      accountId: response.AccountID,
      sessionId: response.SessionID,
      username: response.Username,
      name,
      initials: name.slice(0, 2).toUpperCase(),
      email: response.AccountEmail,
      lang: response.Lang,
    };
    this.persistSession(session, username, password, rememberMe);
    this.i18n.setLang(response.Lang);
    this.settingsService.setFromLogin(response.DateFormat, response.DistanceUnits);
    this.pushNotification.registerPushToken(response.AccountID, response.SessionID);
  }

  private storeAdminSession(response: AdminLoginResponse, username: string, password: string, rememberMe: boolean): void {
    const session: Session = {
      accountId: 0,
      sessionId: '',
      username,
      name: username,
      initials: username.slice(0, 2).toUpperCase(),
      isAdmin: true,
      adminId: response.ID,
      loginId: response.LoginID,
      loginToken: response.LoginToken,
    };
    this.persistSession(session, username, password, rememberMe);
  }

  private persistSession(session: Session, username: string, password: string, rememberMe: boolean): void {
    const serialised = JSON.stringify(session);
    if (rememberMe) {
      localStorage.setItem(this.SESSION_KEY, serialised);
      localStorage.setItem(this.REMEMBER_KEY, username);
      localStorage.setItem(this.REMEMBER_PWD_KEY, password);
      sessionStorage.removeItem(this.SESSION_KEY);
      this.addSavedAccount(username, password, session.initials);
    } else {
      sessionStorage.setItem(this.SESSION_KEY, serialised);
      localStorage.removeItem(this.SESSION_KEY);
      localStorage.removeItem(this.REMEMBER_KEY);
      localStorage.removeItem(this.REMEMBER_PWD_KEY);
    }
    this._currentPassword.set(password);
    this.isAuthenticated.set(true);
    this.currentUser.set(session);
  }

  // ── Saved accounts ────────────────────────────────────────────────────────────
  getSavedAccounts(): SavedAccount[] {
    const raw = localStorage.getItem(this.SAVED_ACCOUNTS_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private addSavedAccount(username: string, password: string, initials: string): void {
    const accounts = this.getSavedAccounts().filter(a => a.username !== username);
    accounts.unshift({ username, password, initials });
    localStorage.setItem(this.SAVED_ACCOUNTS_KEY, JSON.stringify(accounts));
  }

  removeSavedAccount(username: string): void {
    const accounts = this.getSavedAccounts().filter(a => a.username !== username);
    localStorage.setItem(this.SAVED_ACCOUNTS_KEY, JSON.stringify(accounts));
  }

  logout(): void {
    const accountId = this.getAccountId();
    const sessionId = this.getSessionId();
    const serial    = localStorage.getItem('uuid') ?? '';
    if (accountId && sessionId) {
      this.http.post(
        `${environment.API_ENDPOINT}MobileApp/LogOff?AccountID=${accountId}&SessionID=${sessionId}&Serial=${serial}`,
        {},
        { headers: { 'Content-Type': 'application/json' }, responseType: 'text' }
      ).subscribe({ error: () => {} });
    }
    sessionStorage.removeItem(this.SESSION_KEY);
    localStorage.removeItem(this.SESSION_KEY);
    localStorage.removeItem(this.REMEMBER_KEY);
    localStorage.removeItem(this.REMEMBER_PWD_KEY);
    localStorage.removeItem('ble_lang_manual');
    this._currentPassword.set(null);
    this.isAuthenticated.set(false);
    this.currentUser.set(null);
    this.tagService.reset();
    this.titleService.setTitle('Tactical Tracking');
    this.router.navigate(['/login']);
  }

  getSessionId(): string | null {
    return this.currentUser()?.sessionId ?? null;
  }

  getAccountId(): number | null {
    return this.currentUser()?.accountId ?? null;
  }

  isAdmin(): boolean {
    return this.currentUser()?.isAdmin === true;
  }

  getAdminId(): number | null {
    return this.currentUser()?.adminId ?? null;
  }

  getLoginId(): number | null {
    return this.currentUser()?.loginId ?? null;
  }

  getLoginToken(): string | null {
    return this.currentUser()?.loginToken ?? null;
  }

  updateName(name: string): void {
    const session = this.currentUser();
    if (!session) return;
    const updated: Session = { ...session, name, initials: name.slice(0, 2).toUpperCase() };
    const serialised = JSON.stringify(updated);
    if (localStorage.getItem(this.SESSION_KEY)) {
      localStorage.setItem(this.SESSION_KEY, serialised);
    } else {
      sessionStorage.setItem(this.SESSION_KEY, serialised);
    }
    this.currentUser.set(updated);
  }

  /**
   * Met à jour le mot de passe du compte connecté sans déconnecter :
   * en mémoire, dans le mot de passe mémorisé (« se souvenir ») et dans le
   * compte enregistré — pour que la prochaine connexion utilise le nouveau.
   */
  updateCurrentPassword(newPassword: string): void {
    this._currentPassword.set(newPassword);
    if (localStorage.getItem(this.REMEMBER_PWD_KEY) !== null) {
      localStorage.setItem(this.REMEMBER_PWD_KEY, newPassword);
    }
    const username = this.currentUser()?.username;
    if (username) {
      const accounts = this.getSavedAccounts();
      const idx = accounts.findIndex(a => a.username === username);
      if (idx >= 0) {
        accounts[idx] = { ...accounts[idx], password: newPassword };
        localStorage.setItem(this.SAVED_ACCOUNTS_KEY, JSON.stringify(accounts));
      }
    }
  }

  getRememberedUsername(): string | null {
    return localStorage.getItem(this.REMEMBER_KEY);
  }

  getRememberedPassword(): string | null {
    return localStorage.getItem(this.REMEMBER_PWD_KEY);
  }

  getCurrentPassword(): string | null {
    return this._currentPassword() ?? this.getRememberedPassword();
  }

  private hasSession(): boolean {
    return !!(localStorage.getItem(this.SESSION_KEY) || sessionStorage.getItem(this.SESSION_KEY));
  }

  private getStoredSession(): Session | null {
    const raw = localStorage.getItem(this.SESSION_KEY) ?? sessionStorage.getItem(this.SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  }
}
